/**
 * DesignerPreviewCard
 * Compact profile card for a verified designer or contractor, used by the
 * homeowner home surfaces. Renders only fields the profile API returns.
 */

import type {
	ContractorProfile,
	DesignerProfile,
} from "@/features/marketplace/services/marketplace-api.service";
import { cn, resolveMediaUrl } from "@/lib/utils";
import { IMAGE_PLACEHOLDER, swapToPlaceholder } from "../utils/image-fallback";

export interface DesignerPreviewCardProps {
	profile: DesignerProfile | ContractorProfile;
	type: "DESIGNER" | "CONTRACTOR";
	onViewProfile?: (id: string) => void;
}

function getInitials(name: string): string {
	return name
		.split(" ")
		.slice(0, 2)
		.map((n) => n[0]?.toUpperCase() ?? "")
		.join("");
}

function getAvatarStyle(index: number): "warm" | "dark" | "neutral" {
	const styles: ("warm" | "dark" | "neutral")[] = ["warm", "dark", "neutral"];
	return styles[index % 3];
}

export function DesignerPreviewCard({
	profile,
	type,
	onViewProfile,
}: DesignerPreviewCardProps) {
	const name = profile.name;
	const initials = getInitials(name);
	const isDesigner = type === "DESIGNER";
	const designerProfile = isDesigner ? (profile as DesignerProfile) : null;
	const contractorProfile = !isDesigner ? (profile as ContractorProfile) : null;
	const avatarStyle = getAvatarStyle(name.length);

	const style = isDesigner
		? (designerProfile?.styles?.join(" · ") ?? "Interior design")
		: (contractorProfile?.trades?.join(" · ") ?? "Construction");
	const years = isDesigner
		? designerProfile?.experienceYears
			? `${designerProfile?.experienceYears} years`
			: "—"
		: contractorProfile?.experienceYears
			? `${contractorProfile?.experienceYears} years`
			: "—";
	const location = isDesigner
		? (designerProfile?.serviceCities?.join(" · ") ?? "—")
		: (contractorProfile?.serviceAreas?.join(" · ") ?? "—");
	const bio = profile.bio
		? profile.bio.length > 80
			? `${profile.bio.slice(0, 80)}…`
			: profile.bio
		: null;
	const imageUrl = profile.profilePictureUrl
		? resolveMediaUrl(profile.profilePictureUrl)
		: undefined;
	const avatarStyles = {
		warm: "bg-[linear-gradient(135deg,var(--color-border-default),var(--color-dashboard-orange-300))] text-[var(--color-dashboard-orange-text-on-tint)]",
		dark: "bg-[var(--color-dashboard-dark)] text-dashboard-on-fill",
		neutral:
			"bg-[var(--color-dashboard-divider)] text-[var(--color-dashboard-muted-dark)]",
	} as const;
	const avatarBg = avatarStyles[avatarStyle];

	return (
		<div className="overflow-hidden rounded-2xl border border-dashboard-border bg-dashboard-paper dashboard-surface-card-light">
			<div className="relative aspect-16/10 overflow-hidden">
				<img
					src={imageUrl ?? IMAGE_PLACEHOLDER}
					alt={name}
					className="h-full w-full object-cover"
					onError={swapToPlaceholder}
					style={{ filter: "saturate(0.92) contrast(1.06)" }}
				/>
				<div className="absolute inset-0 dashboard-overlay-bottom" />
				<div className="absolute left-4 bottom-4 text-dashboard-on-fill">
					<p className="font-mono text-[9px] font-semibold uppercase tracking-[0.18em] text-dashboard-on-fill/75">
						{type}
						{profile.isVerified ? " · VERIFIED" : ""}
					</p>
					<p className="mt-1 font-display italic font-medium text-[22px] text-dashboard-on-fill tracking-tight">
						{name}.
					</p>
				</div>
			</div>

			<div className="p-5">
				<div className="flex items-center gap-3">
					<div
						className={cn(
							"h-9 w-9 shrink-0 rounded-full flex items-center justify-center font-body text-[11px] font-bold",
							avatarBg,
						)}
						style={{ boxShadow: "var(--dashboard-shadow-avatar)" }}
					>
						{initials}
					</div>
					<div className="flex-1">
						<p className="font-display italic text-[15px] font-medium text-dashboard-text">
							{name}
						</p>
						<p className="font-mono text-[9px] text-dashboard-muted uppercase tracking-[0.14em] mt-0.5">
							{style}
						</p>
					</div>
				</div>

				<div className="flex flex-col gap-3 mt-4 pt-3.5">
					<div className="border-t border-dashboard-border"></div>
					<div className="flex gap-5 ">
						<div className="flex flex-col">
							<p className="font-mono text-[8.5px] font-semibold uppercase tracking-[0.18em] text-dashboard-muted">
								EXPERIENCE
							</p>
							<p className="font-display italic font-medium text-[15px] text-dashboard-text mt-0.5">
								{years}
							</p>
						</div>
						<div className="flex flex-col">
							<p className="font-mono text-[8.5px] font-semibold uppercase tracking-[0.18em] text-dashboard-muted">
								SERVICE AREA
							</p>
							<p className="font-display italic font-medium text-[15px] text-dashboard-text mt-0.5">
								{location}
							</p>
						</div>
					</div>

					{bio && (
						<p className="mt-3.5 font-body text-[13px] leading-[1.55] text-dashboard-muted-dark">
							{bio}
						</p>
					)}
				</div>

				<button
					type="button"
					className="dashboard-primary-button flex h-9 mt-4 w-full items-center justify-center rounded-lg font-body text-[12.5px] font-semibold text-dashboard-on-fill border-none cursor-pointer gap-2 transition-all duration-200"
					onClick={() => onViewProfile?.(profile.id)}
				>
					View profile
				</button>
			</div>
		</div>
	);
}
